import React from 'react';
import { Box, Drawer as MuiDrawer, Stack, Typography, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import Loading from 'views/App/Portal/Loading';
import { error as errorColor, neutral } from '_lex/_foundations/phxColors';
import PanelToolbar from '_lex/_atoms/PanelToolbar';
import PanelFooter from '_lex/_atoms/PanelFooter';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

export interface LeftDrawerProps {
  header?: React.ReactNode;
  headerActions?: React.ReactNode;
  content: React.ReactNode;
  footer?: React.ReactNode;
  footerActions?: React.ReactNode;
  open: boolean;
  isLoading?: boolean;
  hasError?: boolean;
  error?: string;
  setOpen?: (open: boolean) => void;
  noPadding?: boolean;
  hasToolbar?: boolean;
}

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  '& .MuiPaper-root': {
    boxShadow: 'none',
    maxWidth: '30vw',
  },
  '& .MuiDrawer-paper': {
    position: 'relative',
    whiteSpace: 'nowrap',
    width: '360px',
    height: '100%',
    overflow: 'hidden',
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    boxSizing: 'border-box',
    ...(!open && {
      overflowX: 'hidden',
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
      width: '64px',
    }),
  },
}));

const LeftDrawer = ({
  header,
  headerActions,
  content,
  open,
  footer,
  footerActions,
  isLoading,
  hasError,
  error,
  setOpen,
  noPadding = false,
}: LeftDrawerProps) => {
  const showHeader = !!header || !!setOpen || !!headerActions;
  const toggleOpenButton = setOpen ? (
    <Button
      variant='text'
      color='neutral'
      size='small'
      className='icon-only xs'
      onClick={() => setOpen(!open)}
      sx={{ ml: 'auto' }}
    >
      {open ? <ChevronLeftIcon /> : <ChevronRightIcon />}
    </Button>
  ) : null;

  const drawerRows = () => {
    let gridRows = '1fr';
    if (showHeader) gridRows = `auto ${gridRows}`;
    if (footer) gridRows = `${gridRows} auto`;
    return gridRows;
  };

  return (
    <Drawer
      className='left-drawer'
      variant='permanent'
      open={open}
      anchor='left'
      sx={{
        backgroundColor: neutral[25],
        width: open ? '360px' : '64px',
        maxWidth: '30vw',
        transition: 'width 0.5s ease',
        height: '100%',
        borderRadius: 0,
        overflow: 'hidden',
        zIndex: (theme) => theme.zIndex.drawer + 1,
      }}
    >
      {hasError ? (
        <Typography sx={{ color: errorColor[600] }}>{error}</Typography>
      ) : isLoading ? (
        <Box sx={{ mx: 'auto' }}>
          <Loading />
        </Box>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateRows: drawerRows(),
            height: '100%',
          }}
        >
          {showHeader && (
            <PanelToolbar
              leftItems={open ? header : null}
              rightItems={
                <>
                  {open && headerActions}
                  {toggleOpenButton}
                </>
              }
            />
          )}
          <Box
            sx={{
              overflowY: 'auto',
              p: noPadding ? 0 : 4,
              width: '100%',
              maxWidth: '100%',
              minWidth: 0,
              boxSizing: 'border-box',
            }}
          >
            {open && content}
          </Box>
          {footer && (
            <PanelFooter
              leftItems={open ? footer : null}
              rightItems={open ? footerActions : null}
            />
          )}
        </Box>
      )}
    </Drawer>
  );
};

export default LeftDrawer;
